<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/55 px-4 py-6 backdrop-blur-[3px] sm:px-6 sm:py-10"
    role="dialog"
    aria-modal="true"
    aria-labelledby="edit-ws-title"
    @click.self="resetModal"
  >
    <div
      class="relative my-auto flex w-full max-w-lg max-h-[min(92vh,1200px)] flex-col overflow-hidden rounded border border-gray-200/85 text-left shadow-2xl shadow-slate-900/15 ring-1 ring-black/[0.05] animate-scale-in dark:border-gray-600/55 dark:shadow-black/45 dark:ring-white/[0.06]"
      style="background-color: var(--bg-modal); color: var(--text-primary)"
      @click.stop
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
        aria-hidden="true"
      />

      <header
        class="flex shrink-0 items-start gap-4 border-b px-6 pb-4 pt-5"
        style="border-color: var(--border-primary)"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gradient-to-br from-indigo-500/15 to-violet-600/25 text-indigo-600 dark:from-indigo-400/20 dark:to-violet-500/15 dark:text-indigo-400"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </div>
        <div class="min-w-0 flex-1 pt-0.5">
          <h2 id="edit-ws-title" class="text-lg font-semibold tracking-tight" style="color: var(--text-primary)">
            {{ t('editWorkspace.title') }}
          </h2>
          <p class="mt-1 text-sm leading-relaxed" style="color: var(--text-muted)">
            {{ t('editWorkspace.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close shrink-0 rounded-full p-2 transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
          style="color: var(--text-muted)"
          :aria-label="t('common.close')"
          @click="resetModal"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5">
        <form @submit.prevent="handleSubmit" class="space-y-5">
          <div>
            <label
              for="edit-nome"
              class="mb-2 block text-xs font-semibold uppercase tracking-wide"
              style="color: var(--text-tertiary)"
            >
              {{ t('editWorkspace.fields.name') }} <span class="text-rose-500">*</span>
            </label>
            <input
              id="edit-nome"
              v-model="form.nome"
              type="text"
              required
              class="input-field !rounded"
              :placeholder="t('editWorkspace.fields.namePlaceholder')"
              :disabled="workspaceStore.isLoading"
            />
          </div>

          <div>
            <label
              for="edit-descricao"
              class="mb-2 block text-xs font-semibold uppercase tracking-wide"
              style="color: var(--text-tertiary)"
            >
              {{ t('editWorkspace.fields.description') }} <span class="font-normal normal-case opacity-80">{{ t('editWorkspace.fields.optional') }}</span>
            </label>
            <textarea
              id="edit-descricao"
              v-model="form.descricao"
              rows="3"
              class="input-field resize-none !rounded"
              :placeholder="t('editWorkspace.fields.descriptionPlaceholder')"
              :disabled="workspaceStore.isLoading"
            />
          </div>

          <div>
            <label class="mb-3 block text-xs font-semibold uppercase tracking-wide" style="color: var(--text-tertiary)">
              {{ t('editWorkspace.fields.type') }}
            </label>
            <p class="mb-3 text-sm leading-relaxed" style="color: var(--text-muted)">
              {{ t('editWorkspace.fields.typeHint') }}
            </p>
            <div
              class="flex items-center gap-4 rounded border-2 p-4 opacity-95"
              :class="isTarefas ? 'border-indigo-500 bg-indigo-500/[0.08] dark:border-indigo-400 dark:bg-indigo-500/10' : 'border-emerald-500 bg-emerald-500/[0.08] dark:border-emerald-400 dark:bg-emerald-500/10'"
            >
              <div
                class="flex h-12 w-12 shrink-0 items-center justify-center rounded text-white shadow-md"
                :class="isTarefas ? 'bg-[#5865F2] shadow-indigo-500/25' : 'bg-emerald-500 shadow-emerald-500/25'"
              >
                <svg v-if="isTarefas" class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
                <svg v-else class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div class="min-w-0">
                <p class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ tipoLabel }}</p>
                <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                  {{ isTarefas ? t('editWorkspace.types.tasks.description') : t('editWorkspace.types.crm.description') }}
                </p>
              </div>
            </div>
          </div>

          <WorkspaceIconSelector
            v-model="workspaceVisual"
            :disabled="workspaceStore.isLoading"
            @update:model-value="handleWorkspaceVisualUpdate"
          />

          <ColorSelector v-model="form.corWorkspace" color-context="workspace" :disabled="workspaceStore.isLoading" />

          <div
            class="flex flex-col-reverse gap-2 border-t pt-5 sm:flex-row sm:justify-end sm:gap-3"
            style="border-color: var(--border-primary)"
          >
            <button
              type="button"
              class="btn-secondary flex-1 !rounded py-2.5 sm:flex-initial sm:min-w-[7.5rem]"
              :disabled="workspaceStore.isLoading"
              @click="resetModal"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="btn-primary flex-1 !rounded py-2.5 font-semibold shadow-lg shadow-indigo-500/20 transition-all hover:shadow-indigo-500/30 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[10rem]"
              :disabled="workspaceStore.isLoading || !isFormValid"
            >
              <span v-if="workspaceStore.isLoading" class="flex items-center justify-center gap-2">
                <svg class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {{ t('editWorkspace.submit.saving') }}
              </span>
              <span v-else>{{ t('editWorkspace.submit.save') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useWorkspaceStore } from '../stores/workspace'
import WorkspaceIconSelector from './WorkspaceIconSelector.vue'
import ColorSelector from './ColorSelector.vue'

const props = defineProps({
  workspace: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'updated'])

const resetModal = () => {
  isInitialized = false
  emit('close')
}

const { t } = useI18n()
const workspaceStore = useWorkspaceStore()

const form = reactive({
  nome: '',
  descricao: '',
  corWorkspace: '#5865F2'
})

const workspaceVisual = reactive({
  type: 'icon',
  icon: 'task',
  image: ''
})

const isTarefas = computed(() => String(props.workspace?.tipo || '').toUpperCase() === 'TAREFAS')
const tipoLabel = computed(() =>
  isTarefas.value ? t('editWorkspace.types.tasks.title') : t('editWorkspace.types.crm.title')
)

let isInitialized = false

const handleWorkspaceVisualUpdate = (newValue) => {
  workspaceVisual.type = newValue.type
  workspaceVisual.icon = newValue.icon
  workspaceVisual.image = newValue.image
}

const isFormValid = computed(() => {
  return form.nome.trim()
})

watch(
  () => props.workspace,
  (newWorkspace) => {
    if (newWorkspace && !isInitialized) {
      form.nome = newWorkspace.nome || ''
      form.descricao = newWorkspace.descricao || ''
      form.corWorkspace = newWorkspace.corWorkspace || '#5865F2'

      if (newWorkspace.fotoWorkspace && newWorkspace.fotoWorkspace.trim()) {
        workspaceVisual.type = 'upload'
        workspaceVisual.image = newWorkspace.fotoWorkspace
        workspaceVisual.icon = newWorkspace.icone || 'task'
      } else {
        workspaceVisual.type = 'icon'
        workspaceVisual.icon = newWorkspace.icone || 'task'
        workspaceVisual.image = ''
      }

      isInitialized = true
    }
  },
  { immediate: true }
)

const handleSubmit = async () => {
  let fotoWorkspaceUrl = ''
  if (workspaceVisual.type === 'upload' && workspaceVisual.image && workspaceVisual.image.trim()) {
    fotoWorkspaceUrl = workspaceVisual.image
  }

  const workspaceData = {
    nome: form.nome,
    descricao: form.descricao,
    tipo: props.workspace.tipo,
    corWorkspace: form.corWorkspace,
    fotoWorkspace: fotoWorkspaceUrl,
    icone: workspaceVisual.type === 'icon' ? workspaceVisual.icon : 'task'
  }

  const result = await workspaceStore.updateWorkspace(props.workspace.id, workspaceData)

  if (result.success) {
    emit('updated', result.workspace)
    resetModal()
  } else {
    console.error('Erro ao atualizar workspace:', result.error)
  }
}
</script>
